package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume

enum class LPresencePhase(val attributeValue: String) {
    HIDDEN("hidden"),
    ENTERING("entering"),
    PRESENT("present"),
    EXITING("exiting"),
}

/**
 * A surface lifecycle that separates "requested closed" from "safe to unmount".
 *
 * [generation] makes delayed completion idempotent: an exit timer from an earlier close cannot
 * remove a surface that reopened before the timer fired.
 */
data class LPresenceState(
    val rendered: Boolean,
    val phase: LPresencePhase,
    val generation: Long,
) {
    fun request(open: Boolean): LPresenceState = when {
        open && (phase == LPresencePhase.PRESENT || phase == LPresencePhase.ENTERING) -> this
        !open && (phase == LPresencePhase.HIDDEN || phase == LPresencePhase.EXITING) -> this
        open -> copy(rendered = true, phase = LPresencePhase.ENTERING, generation = generation + 1)
        else -> copy(rendered = true, phase = LPresencePhase.EXITING, generation = generation + 1)
    }

    fun finishEnter(requestGeneration: Long): LPresenceState = if (
        generation == requestGeneration && phase == LPresencePhase.ENTERING
    ) {
        copy(phase = LPresencePhase.PRESENT)
    } else {
        this
    }

    fun finishExit(requestGeneration: Long): LPresenceState = if (
        generation == requestGeneration && phase == LPresencePhase.EXITING
    ) {
        copy(rendered = false, phase = LPresencePhase.HIDDEN)
    } else {
        this
    }

    companion object {
        fun initial(open: Boolean): LPresenceState = LPresenceState(
            rendered = open,
            phase = if (open) LPresencePhase.PRESENT else LPresencePhase.HIDDEN,
            generation = 0,
        )
    }
}

@Composable
fun rememberLPresence(
    open: Boolean,
    exitDurationMillis: Int,
    settleImmediately: Boolean = false,
): LPresenceState {
    // Instant surfaces (the command palette) are deliberately synchronous: a Compose effect can
    // run a frame later under load, briefly leaving a closed dialog mounted and able to catch
    // focus. They do not need generation tracking because they have no visual exit to protect.
    if (settleImmediately) return LPresenceState.initial(open)

    var state by remember { mutableStateOf(LPresenceState.initial(open)) }
    LaunchedEffect(open, exitDurationMillis, settleImmediately) {
        val requested = state.request(open)
        state = requested
        val reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        when (requested.phase) {
            LPresencePhase.ENTERING -> {
                if (!reducedMotion) {
                    suspendCancellableCoroutine<Unit> { continuation ->
                        window.requestAnimationFrame {
                            if (continuation.isActive) continuation.resume(Unit)
                        }
                    }
                }
                state = state.finishEnter(requested.generation)
            }

            LPresencePhase.EXITING -> {
                if (!reducedMotion) delay(exitDurationMillis.toLong())
                state = state.finishExit(requested.generation)
            }

            LPresencePhase.HIDDEN, LPresencePhase.PRESENT -> Unit
        }
    }
    return state
}
